import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useOutletContext } from 'react-router-dom';
import { Copy, Loader2, Mail, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { adminAPI, apiError } from '@/lib/adminApi';

const STATUS_LABEL = { active: 'Active', invited: 'Invited', disabled: 'Access off' };

/**
 * Superuser only: who can use the portal, and inviting more agents.
 * Agents answer chats; they can't invite. When email isn't set up the
 * invite link is shown here to copy and send.
 */
export default function AdminTeamPage() {
  const { admin } = useOutletContext() || {};
  const [data, setData] = useState({ admins: [], email_configured: false });
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [link, setLink] = useState(null);

  const load = useCallback(async () => {
    const res = await adminAPI.admins();
    setData(res.data);
  }, []);

  useEffect(() => { void load().catch(() => {}); }, [load]);

  if (admin && admin.role !== 'superuser') return <Navigate to="/admin/chats" replace />;

  const showResult = (res) => {
    setLink(res.data.email_sent ? null : { email: res.data.admin.email, url: res.data.invite_link });
  };

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await adminAPI.invite(email.trim(), name.trim());
      showResult(res);
      setEmail('');
      setName('');
      await load();
    } catch (err) {
      setError(apiError(err, 'Could not send the invite.'));
    } finally {
      setBusy(false);
    }
  };

  const resend = async (row) => {
    try {
      showResult(await adminAPI.resendInvite(row.id));
    } catch (err) {
      setError(apiError(err, 'Could not resend the invite.'));
    }
  };

  const resetAuthenticator = async (row) => {
    if (!window.confirm(`Reset ${row.name || row.email}'s authenticator? They will be signed out and set up a new one next time they sign in.`)) return;
    try {
      await adminAPI.resetAuthenticator(row.id);
      await load();
    } catch (err) {
      setError(apiError(err, 'Could not reset the authenticator.'));
    }
  };

  const toggle = async (row) => {
    await adminAPI.setAdminAccess(row.id, row.status === 'disabled');
    await load();
  };

  return (
    <div className="p-4 max-w-3xl" data-testid="admin-team">
      <h1 className="font-heading text-xl font-bold text-farm-forest mb-1">Team</h1>
      <p className="text-sm text-farm-forest/70 mb-4">
        People who answer support chats. Agents can reply to users but can't invite anyone.
      </p>

      <form onSubmit={invite} className="bg-white rounded-2xl p-4 shadow-sm mb-4 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
        <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required
               data-testid="admin-invite-email" />
        <Input placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
        <Button type="submit" className="bg-farm-primary" disabled={busy} data-testid="admin-invite-send">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1" /> Invite</>}
        </Button>
        {error && <p className="text-sm text-red-600 sm:col-span-3" role="alert">{error}</p>}
        {!data.email_configured && (
          <p className="text-xs text-farm-forest/60 sm:col-span-3">
            Email isn&apos;t set up yet, so invites aren&apos;t emailed: copy the link and send it yourself.
          </p>
        )}
      </form>

      {link && (
        <div className="bg-farm-gold/10 border border-farm-gold/40 rounded-2xl p-4 mb-4" data-testid="admin-invite-link">
          <p className="text-sm font-semibold text-farm-forest">Invite link for {link.email}</p>
          <p className="text-xs text-farm-forest/70 mb-2">Works once and expires in 7 days. Send it only to them.</p>
          <div className="flex gap-2">
            <Input readOnly value={link.url} onFocus={(e) => e.target.select()} />
            <Button type="button" variant="outline" onClick={() => navigator.clipboard?.writeText(link.url)}>
              <Copy className="w-4 h-4 mr-1" /> Copy
            </Button>
          </div>
        </div>
      )}

      <ul className="bg-white rounded-2xl shadow-sm divide-y divide-gray-100" data-testid="admin-team-list">
        {data.admins.map((row) => (
          <li key={row.id} className="p-3 flex items-center gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-farm-forest truncate">{row.name || row.email}</p>
              <p className="text-xs text-farm-forest/60 truncate">
                {row.email} · {row.role === 'superuser' ? 'Owner' : 'Agent'} · {STATUS_LABEL[row.status]}
                {row.status === 'active' && ` · Authenticator ${row.authenticator ? 'on' : 'not set up'}`}
              </p>
            </div>
            {row.role === 'agent' && (
              <div className="ml-auto flex gap-2">
                {row.status === 'invited' && (
                  <Button size="sm" variant="outline" onClick={() => resend(row)}>
                    <Mail className="w-4 h-4 mr-1" /> Resend invite
                  </Button>
                )}
                {row.authenticator && (
                  <Button size="sm" variant="outline" onClick={() => resetAuthenticator(row)}
                          data-testid={`admin-reset-authenticator-${row.id}`}>
                    Reset authenticator
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => toggle(row)}>
                  {row.status === 'disabled' ? 'Turn access on' : 'Turn access off'}
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
